import { useState } from "react";
import styled, { keyframes } from "styled-components";
import { MessageCircle, X, Utensils, ConciergeBell } from "lucide-react";
import { useLandingLanguage } from "../../../contexts/landing-language";

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const Wrapper = styled.div`
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;

  @media (max-width: 480px) {
    bottom: 20px;
    right: 16px;
  }
`;

const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #e5e1dc;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  width: 280px;
  animation: ${slideUp} 0.25s ease forwards;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 260px;
  }
`;

const PanelHeader = styled.div`
  background: #1a1a1a;
  padding: 18px 20px;
`;

const PanelTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #c9a86c;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const PanelSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 300;
  line-height: 1.5;
`;

const PanelBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e5e1dc;
  color: #1a1a1a;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: #faf8f5;
    border-color: #c9a86c;
    color: #1a1a1a;
  }

  svg {
    color: #c9a86c;
    flex-shrink: 0;
  }
`;

const LinkText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LinkTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const LinkDesc = styled.span`
  font-size: 11px;
  color: #8b7355;
  font-weight: 400;
`;

const ToggleButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #1a1a1a;
  border: none;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;

  &:hover {
    background: #c9a86c;
    transform: scale(1.08);
    box-shadow: 0 12px 32px rgba(201, 168, 108, 0.4);
  }
`;

const GuestWidget = () => {
  const [open, setOpen] = useState(true);
  const { t } = useLandingLanguage();
  const gw = t.guestWidget;

  return (
    <Wrapper>
      {open && (
        <Panel>
          <PanelHeader>
            <PanelTitle>{gw.title}</PanelTitle>
            <PanelSubtitle>{gw.subtitle}</PanelSubtitle>
          </PanelHeader>
          <PanelBody>
            <ServiceLink href="/hebe/001/hgs">
              <ConciergeBell size={20} />
              <LinkText>
                <LinkTitle>{gw.guestServices}</LinkTitle>
                <LinkDesc>{gw.guestServicesDesc}</LinkDesc>
              </LinkText>
            </ServiceLink>
            <ServiceLink href="/hebe/001/hgs/fnb/">
              <Utensils size={20} />
              <LinkText>
                <LinkTitle>{gw.orderFood}</LinkTitle>
                <LinkDesc>{gw.orderFoodDesc}</LinkDesc>
              </LinkText>
            </ServiceLink>
          </PanelBody>
        </Panel>
      )}

      <ToggleButton onClick={() => setOpen((v) => !v)} aria-label="Guest services">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </ToggleButton>
    </Wrapper>
  );
};

export default GuestWidget;
